// src/components/views/SuggestiveLabelsTabs.tsx

import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Modal, Button, Col, Row, Card, Tabs, Tab } from 'react-bootstrap'
import styled from 'styled-components'
import { resetImageLoadingQueue } from '../../../../hooks/useContentImage'
import { useRunBatchInferenceMutation, useGetModelQuery } from '../../../../services/modelsApi'
import { useQueryPredictionsQuery } from '../../../../services/predictionApi'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import { useGetTagsQuery } from '../../../../services/tagApi'
import { useFetchAnnotatedContentQuery } from '../../../../services/contentApi'
import { useAppSelector, useAppDispatch } from '../../../../store/hooks'
import { selectSelectedModelId } from '../../../../store/slices/modelSlice'
import {
  selectActiveConfidenceLevel,
  setActiveConfidenceLevel,
} from '../../../../store/slices/predictionSlice'
import { selectSelectedProjectId } from '../../../../store/slices/projectSlice'
import {
  selectConfidenceThresholds,
  setConfidenceThresholds,
  resetConfidenceThresholds,
} from '../../../../store/slices/uiSettingsSlice'
import { QueryPredictionRequest } from '../../../../types/prediction'
import ErrorDialog from '../../../modals/ErrorDialog'
import ModelAssistedLabeling from '../../../tools/ModelAssistedLabeling'
import PredictionsGalleryView from '../../PredictionsGalleryView'
import { FaChartBar, FaChevronDown, FaChevronUp } from 'react-icons/fa'
import InferenceStats from './InferenceStats'
import { Grid3x3, Grid2x2, LayoutGrid } from 'lucide-react'
import ReviewProgressCard from './ReviewProgressCard'

const MainContainer = styled.div`
  height: 65vh;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow: hidden;
`

const HeaderContainer = styled.div`
  flex-shrink: 0;
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  position: relative;
`

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  gap: 1rem;
  min-height: 0;
  overflow: hidden;
`

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: rgba(42, 42, 42, 0.4);
  border: 1px solid rgba(147, 51, 234, 0.2);
  border-radius: 12px;
  padding: 1rem;
  overflow: hidden;
`

const RightSection = styled.div`
  width: 320px;
  flex-shrink: 0;
  overflow-y: auto;
`

const GalleryContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const StatsOverlayContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  width: 400px;
  background: #1a1a1a;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 100;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transform: translateY(${(props) => (props.$isOpen ? '0' : '-10px')});
  transition: all 0.3s ease-in-out;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
  margin-bottom: 0.75rem;
`

const StyledTabs = styled(Tabs)`
  &.nav-tabs {
    border: none;
    gap: 0.4rem;
    margin-bottom: 0;
    font-size: 0.8rem;
    padding: 0.3rem;
    background: rgba(42, 42, 42, 0.6);
    border-radius: 8px;
    backdrop-filter: blur(10px);
    flex: 1;

    .nav-item {
      margin: 0;
      display: flex;
      align-items: center;
    }

    .nav-link {
      border: 1px solid transparent;
      background: rgba(60, 60, 60, 0.3);
      color: #a0a0a0;
      border-radius: 6px;
      padding: 0.4rem 0.6rem;
      transition: all 0.3s ease;
      margin: 0;
      position: relative;
      overflow: hidden;
      font-weight: 500;
      backdrop-filter: blur(5px);

      &:hover {
        background: rgba(74, 74, 74, 0.6);
        border-color: rgba(147, 51, 234, 0.3);
        color: #ffffff;
        transform: translateY(-1px);
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
      }

      &.active {
        background: linear-gradient(135deg, #9333ea 0%, #c084fc 100%);
        border-color: #9333ea;
        color: #ffffff;
        font-weight: 600;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(147, 51, 234, 0.4);
      }
    }

    /* None tab */
    .nav-item:first-child .nav-link {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 16px 16px 0;
        border-color: transparent #dc3545 transparent transparent;
        transition: all 0.2s ease;
        opacity: 0.5;
      }

      &:hover::after {
        opacity: 0.8;
      }

      &.active::after {
        opacity: 1;
        border-width: 0 18px 18px 0;
      }

      &.active {
        border: 1px solid rgba(220, 53, 69, 0.5);
      }
    }

    /* Low confidence tab */
    .nav-item:nth-child(2) .nav-link {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 16px 16px 0;
        border-color: transparent #fd7e14 transparent transparent;
        transition: all 0.2s ease;
        opacity: 0.5;
      }

      &:hover::after {
        opacity: 0.8;
      }

      &.active::after {
        opacity: 1;
        border-width: 0 18px 18px 0;
      }

      &.active {
        border: 1px solid rgba(253, 126, 20, 0.5);
      }
    }

    /* Medium confidence tab */
    .nav-item:nth-child(3) .nav-link {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 16px 16px 0;
        border-color: transparent #ffc107 transparent transparent;
        transition: all 0.2s ease;
        opacity: 0.5;
      }

      &:hover::after {
        opacity: 0.8;
      }

      &.active::after {
        opacity: 1;
        border-width: 0 18px 18px 0;
      }

      &.active {
        border: 1px solid rgba(255, 193, 7, 0.5);
      }
    }

    /* High confidence tab */
    .nav-item:last-child .nav-link {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 16px 16px 0;
        border-color: transparent #198754 transparent transparent;
        transition: all 0.2s ease;
        opacity: 0.5;
      }

      &:hover::after {
        opacity: 0.8;
      }

      &.active::after {
        opacity: 1;
        border-width: 0 18px 18px 0;
      }

      &.active {
        border: 1px solid rgba(25, 135, 84, 0.5);
      }
    }
  }
`

const TabCount = styled.span`
  background: rgba(255, 255, 255, 0.15);
  padding: 0.2rem 0.4rem;
  border-radius: 12px;
  margin-left: 0.4rem;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  .nav-link.active & {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }

  .nav-link:hover & {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.15);
  }
`

const NoDataCard = styled.div`
  background: transparent;
  border: none;
  max-width: 800px;
  margin: 0 auto;
  height: auto;
  min-height: 400px;
  background: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
  }

  .icon {
    font-size: 4rem;
    color: #4dabf7;
    opacity: 0.7;
    margin-bottom: 1.5rem;
  }

  .title {
    font-size: 1.5rem;
    font-weight: 500;
    color: #ffffff;
    margin-bottom: 1rem;
  }

  .description {
    font-size: 1rem;
    color: #8c8c8c;
    max-width: 500px;
    margin-bottom: 2rem;
  }

  .action-button {
    &.btn-primary {
      background: #4dabf7;
      border-color: #4dabf7;
      padding: 0.75rem 2rem;
      font-weight: 500;

      &:hover:not(:disabled) {
        background: #2196f3;
        border-color: #2196f3;
      }

      &:disabled {
        background: #4dabf7;
        border-color: #4dabf7;
        opacity: 0.5;
      }
    }
  }

  .status {
    margin-top: 1rem;
    font-size: 0.9rem;
    color: #6c757d;
  }
`

export const GradientButton = styled(Button).attrs({
  as: 'button',
})`
  background: linear-gradient(45deg, #402563, #114ee0);
  border: none;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  color: white;

  &:hover:not(:disabled) {
    background: linear-gradient(45deg, #4c2d77, #1857e6);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    color: white;
  }

  &:disabled {
    background: linear-gradient(45deg, #402563, #114ee0);
    opacity: 0.5;
    color: white;
  }
`

const ViewToggleGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`

const IconButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${(props) =>
    props.$active ? 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)' : 'rgba(42, 42, 42, 0.6)'};
  border: 1px solid
    ${(props) => (props.$active ? 'rgba(147, 51, 234, 0.5)' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 8px;
  color: ${(props) => (props.$active ? '#ffffff' : '#b0b0b0')};
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: ${(props) =>
      props.$active
        ? 'linear-gradient(135deg, #a855f7 0%, #d8b4fe 100%)'
        : 'rgba(147, 51, 234, 0.15)'};
    border-color: rgba(147, 51, 234, 0.5);
    color: #ffffff;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

const StatsToggleButton = styled.button`
  background: rgba(42, 42, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #9333ea;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  backdrop-filter: blur(10px);
  white-space: nowrap;

  &:hover {
    color: #c084fc;
    background: rgba(147, 51, 234, 0.15);
    border-color: rgba(147, 51, 234, 0.3);
    transform: translateY(-1px);
  }

  svg {
    transition: transform 0.2s ease;
  }
`

const CollapsibleStats = styled.div`
  padding: 0;
`

const InferenceAALSubView: React.FC = (): JSX.Element => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const dispatch = useAppDispatch()
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const confidenceRanges = useAppSelector(selectConfidenceThresholds)
  const activeConfidenceLevel = useAppSelector(selectActiveConfidenceLevel)
  const [showModelModal, setShowModelModal] = useState(false)
  const [showConfidenceSettings, setShowConfidenceSettings] = useState(false)
  const [tempConfidenceRanges, setTempConfidenceRanges] = useState(confidenceRanges)
  const [isActuallyLoading, setIsActuallyLoading] = useState(true)
  const [elapsedTime, setElapsedTime] = useState<string>('')
  const BBOX_THRESHOLD = 0.1
  const [isStatsOpen, setIsStatsOpen] = useState(false)
  const [imagesPerRow, setImagesPerRow] = useState(5)

  // API Hooks
  const [runBatchInference, { isLoading: isBatchRunning }] = useRunBatchInferenceMutation()

  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
    pollingInterval: 60000, // Poll every 1.5 seconds
  })

  const queryParams: QueryPredictionRequest = useMemo(
    () => ({
      query: {
        filters: [{ key: 'modelid', value: selectedModel?.id || '' }],
        sort_key: 'contentid',
        sort_val: -1,
        operator: 'or',
        page: 0,
        limit: 10000, // Set a very high limit to get all predictions
      },
      filter_annotated_content: {
        enable: true,
        projectid: selectedProjectId || '',
        datasetid: project?.datasetid || '',
      },
      uncertainty_threshold: BBOX_THRESHOLD,
      ...(selectedModel?.batch?.metadata_filter?.datasource?.length
        ? {
            metadata_filter: {
              datasource: selectedModel?.batch?.metadata_filter?.datasource,
            },
          }
        : {}),
    }),
    [
      selectedProjectId,
      project?.datasetid,
      selectedModel?.id,
      BBOX_THRESHOLD,
      selectedModel?.batch?.metadata_filter?.datasource,
    ]
  )

  const { data: allPredictions, isLoading: isPredictionsLoading } = useQueryPredictionsQuery(
    queryParams,
    {
      skip: !selectedModelId,
    }
  )

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  // Fetch annotated content (reviewed images with accepted predictions) that match this batch's datasources
  const { data: annotatedContent } = useFetchAnnotatedContentQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      limit: 1000,
      // Apply the same metadata filter as the batch to get only reviewed images from THIS batch
      metadata_filter: selectedModel?.batch?.metadata_filter?.datasource?.length
        ? { datasource: selectedModel?.batch?.metadata_filter?.datasource }
        : undefined,
    },
    {
      skip: !selectedProjectId || !project?.datasetid,
    }
  )

  const confidenceDistribution = useMemo(() => {
    if (!allPredictions?.predictions) return null

    const stats = { none: 0, high: 0, medium: 0, low: 0 }

    allPredictions.predictions?.forEach((prediction) => {
      if (!prediction.predictions || prediction.predictions.length === 0) {
        stats.none++
      } else {
        // Count each image only once based on its highest confidence prediction
        const maxConfidence = Math.max(
          ...prediction.predictions.map((pred) => pred.confidence || 0)
        )
        if (maxConfidence >= confidenceRanges.high) stats.high++
        else if (maxConfidence >= confidenceRanges.medium) stats.medium++
        else stats.low++
      }
    })

    return stats
  }, [allPredictions?.predictions, confidenceRanges])

  const filteredCounts = useMemo(() => {
    if (!allPredictions?.predictions) return { none: 0, low: 0, medium: 0, high: 0 }

    const predictions = allPredictions.predictions
    return {
      none: predictions?.filter((p) => !p.predictions || p.predictions.length === 0).length,
      low: predictions?.filter((p) => {
        if (!p.predictions || p.predictions.length === 0) return false
        const maxConfidence = Math.max(...p.predictions.map((pred) => pred.confidence || 0))
        return maxConfidence >= BBOX_THRESHOLD && maxConfidence < confidenceRanges.medium
      }).length,
      medium: predictions?.filter((p) => {
        if (!p.predictions || p.predictions.length === 0) return false
        const maxConfidence = Math.max(...p.predictions.map((pred) => pred.confidence || 0))
        return maxConfidence >= confidenceRanges.medium && maxConfidence < confidenceRanges.high
      }).length,
      high: predictions?.filter((p) => {
        if (!p.predictions || p.predictions.length === 0) return false
        const maxConfidence = Math.max(...p.predictions.map((pred) => pred.confidence || 0))
        return maxConfidence >= confidenceRanges.high
      }).length,
    }
  }, [allPredictions?.predictions, BBOX_THRESHOLD, confidenceRanges])

  // Filter predictions based on confidence level
  const filteredPredictionsByConfidence = useMemo(() => {
    if (!allPredictions?.predictions) return []

    switch (activeConfidenceLevel) {
      case 'none':
        return allPredictions.predictions?.filter(
          (prediction) => !prediction.predictions || prediction.predictions.length === 0
        )
      case 'high':
        return allPredictions.predictions?.filter((prediction) => {
          if (!prediction.predictions || prediction.predictions.length === 0) return false
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence >= confidenceRanges.high
        })
      case 'medium':
        return allPredictions.predictions?.filter((prediction) => {
          if (!prediction.predictions || prediction.predictions.length === 0) return false
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence >= confidenceRanges.medium && maxConfidence < confidenceRanges.high
        })
      case 'low':
        return allPredictions.predictions?.filter((prediction) => {
          if (!prediction.predictions || prediction.predictions.length === 0) return false
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence >= BBOX_THRESHOLD && maxConfidence < confidenceRanges.medium
        })
      default:
        return []
    }
  }, [allPredictions?.predictions, activeConfidenceLevel, confidenceRanges, BBOX_THRESHOLD])

  // Add state to track if we're in a fresh batch run
  const [isNewBatchStarted, setIsNewBatchStarted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  const handleOpenSettings = () => {
    setTempConfidenceRanges(confidenceRanges)
    setShowConfidenceSettings(true)
  }

  const handleSaveSettings = () => {
    dispatch(setConfidenceThresholds(tempConfidenceRanges))
    setShowConfidenceSettings(false)
  }

  const handleResetSettings = () => {
    dispatch(resetConfidenceThresholds())
    setTempConfidenceRanges(confidenceRanges)
  }

  const handleTabChange = (k: string | null) => {
    if (k) {
      const newTab = k as 'none' | 'high' | 'medium' | 'low'
      dispatch(setActiveConfidenceLevel(newTab))
    }
  }

  const handleRunBatchInference = async () => {
    if (!selectedModel || !selectedProjectId || !project?.datasetid) return

    try {
      // Set the new batch flag to true
      setIsNewBatchStarted(true)

      await runBatchInference({
        id: selectedModel.id,
        project_id: selectedProjectId,
        dataset_id: project.datasetid,
        thumbnail_size: 640,
        metadata_filter: selectedModel?.batch?.metadata_filter?.datasource?.length
          ? { datasource: selectedModel?.batch?.metadata_filter?.datasource }
          : undefined,
        threshold: BBOX_THRESHOLD,
      }).unwrap()

      // Immediately refetch the model to get updated status
      // await refetchModel()
    } catch (error) {
      setErrorMessage(
        error
          ? (error as any).data?.message || 'Failed to run batch inference'
          : 'An unexpected error occurred'
      )
      setShowError(true)
      // Reset the new batch flag if there's an error
      setIsNewBatchStarted(false)
    }
  }

  // Effect to reset the new batch flag when batch is complete
  useEffect(() => {
    if (selectedModel?.batch?.status?.toLowerCase().startsWith('complete')) {
      setIsNewBatchStarted(false)
    }
  }, [selectedModel?.batch?.status])

  // Effect to set the initial active tab based on the confidence distribution
  useEffect(() => {
    if (!activeConfidenceLevel && confidenceDistribution) {
      if (confidenceDistribution.none > 0) {
        dispatch(setActiveConfidenceLevel('none'))
      } else if (confidenceDistribution.low > 0) {
        dispatch(setActiveConfidenceLevel('low'))
      } else if (confidenceDistribution.medium > 0) {
        dispatch(setActiveConfidenceLevel('medium'))
      } else if (confidenceDistribution.high > 0) {
        dispatch(setActiveConfidenceLevel('high'))
      }
    }
  }, [activeConfidenceLevel, confidenceDistribution, dispatch])

  // Track actual loading state of predictions
  useEffect(() => {
    const predictions = allPredictions?.predictions

    if (selectedModelId && isPredictionsLoading && predictions?.length === undefined) {
      setIsActuallyLoading(true)
      return
    } else {
      setIsActuallyLoading(false)
    }

    // Create an array of image loading promises
    if (allPredictions?.predictions) {
      const imageLoadPromises = allPredictions.predictions.map((prediction) => {
        return new Promise((resolve) => {
          const img = new Image()
          img.onload = () => resolve(true)
          img.onerror = () => resolve(false)
          // Handle both base64 images and regular URLs
          img.src = prediction.b64_image
            ? `data:image/jpeg;base64,${prediction.b64_image}`
            : prediction.contentid
        })
      })

      // Wait for all images to load
      Promise.all(imageLoadPromises).then(() => {
        setIsActuallyLoading(false)
      })
    }
  }, [isPredictionsLoading, allPredictions, selectedModelId])

  // Modify the render logic to show empty states when a new batch starts or dataset config changes
  const shouldShowEmptyState =
    isNewBatchStarted ||
    selectedModel?.batch?.status.toLowerCase() === 'running' ||
    selectedModel?.batch?.status.toLowerCase() === 'initialized'

  const renderPredictionsContent = () => {
    if (shouldShowEmptyState) {
      // Determine the appropriate message based on the reason for empty state
      let message = 'Processing batch...'
      let showSpinner = true

      if (isNewBatchStarted || selectedModel?.batch?.status.toLowerCase() === 'running') {
        message = selectedModel?.batch?.status || 'Processing batch...'
        showSpinner = true
      } else if (selectedModel?.batch?.status.toLowerCase() === 'initialized') {
        message = 'Initializing batch inference...'
        showSpinner = true
      }

      return (
        <Row
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '200px' }}
        >
          <Col className="text-center">
            {showSpinner && (
              <div
                className="spinner-border text-primary"
                style={{ width: '3rem', height: '3rem' }}
                role="status"
              />
            )}
            <p className="mt-3 fs-5 fw-bold text-primary">{message}</p>
          </Col>
        </Row>
      )
    }

    return (
      selectedModel?.batch?.status?.toLowerCase().startsWith('complete') && (
        <div
          style={{
            flex: 1,
            height: '500px',
            minHeight: '500px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <PredictionsGalleryView
            key={`predictions-${BBOX_THRESHOLD}-${activeConfidenceLevel}`}
            filteredPredictions={filteredPredictionsByConfidence}
            arePredictionsLoading={isPredictionsLoading}
            imagesPerRow={imagesPerRow}
            bboxThreshold={BBOX_THRESHOLD}
            maxConcurrentRequests={10}
          />
        </div>
      )
    )
  }

  // Add this effect to reset the image loading queue when the component unmounts
  useEffect(() => {
    return () => {
      resetImageLoadingQueue()
    }
  }, [])

  // Calculate elapsed time since batch started
  const calculateElapsedTime = useCallback(() => {
    if (!selectedModel?.batch?.started_at) return ''

    const startTime = new Date(selectedModel.batch.started_at)
    const now = new Date()
    const diffMs = now.getTime() - startTime.getTime()

    const minutes = Math.floor(diffMs / (1000 * 60))
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000)

    return `${minutes}m ${seconds}s`
  }, [selectedModel?.batch?.started_at])

  // Effect to update elapsed time every second when batch is running
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null

    if (shouldShowEmptyState && selectedModel?.batch?.started_at) {
      // Initial calculation
      setElapsedTime(calculateElapsedTime())

      // Update every second
      intervalId = setInterval(() => {
        setElapsedTime(calculateElapsedTime())
      }, 1000)
    } else {
      setElapsedTime('')
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [shouldShowEmptyState, selectedModel?.batch?.started_at, calculateElapsedTime])

  return (
    <>
      {isActuallyLoading ? (
        <NoDataCard>
          <div className="content">
            <div className="icon">
              <div className="spinner-border" role="status" />
            </div>
            <div className="title">Loading Predictions</div>
            <div className="description">
              Please wait while we load your model predictions and images...
            </div>
          </div>
        </NoDataCard>
      ) : shouldShowEmptyState ? (
        <NoDataCard>
          <div className="content">
            <div className="icon">
              <div className="spinner-border" role="status" />
            </div>
            <div className="title">Processing Batch AAL</div>
            <div className="description">
              We&apos;re analyzing your data to identify images that would benefit most from human
              review. This helps focus your labeling efforts where they&apos;ll have the biggest
              impact.
            </div>
            <div className="status">{selectedModel?.batch?.status || 'Processing...'}</div>
            {selectedModel?.batch?.started_at && <div className="status">{elapsedTime}</div>}
          </div>
        </NoDataCard>
      ) : !allPredictions?.predictions || allPredictions.predictions.length === 0 ? (
        <NoDataCard>
          <div className="content">
            <div className="icon">
              <FaChartBar />
            </div>
            <div className="title">Start Adaptive Active Labeling</div>
            <div className="description">
              {selectedModel?.deployment.status.toLowerCase() !== 'in_service'
                ? 'Deploy your model to begin the active labeling process. This will help identify uncertain predictions that need human review, making your labeling workflow more efficient.'
                : 'Run your first batch to start the active labeling process. The model will analyze your data and identify images that need human review, helping you focus on the most impactful labeling tasks.'}
            </div>
            <Button
              className="action-button"
              disabled={
                selectedModel?.deployment.status.toLowerCase() !== 'in_service' || isBatchRunning
              }
              onClick={handleRunBatchInference}
            >
              {selectedModel?.deployment.status.toLowerCase() !== 'in_service'
                ? 'Deploy Model First'
                : 'Start First Batch'}
            </Button>
          </div>
        </NoDataCard>
      ) : (
        <MainContainer>
          <HeaderContainer>
            <ControlsRow>
              <StyledTabs activeKey={activeConfidenceLevel} onSelect={handleTabChange}>
                <Tab
                  disabled={confidenceDistribution?.none === 0}
                  eventKey="none"
                  title={
                    <span>
                      None
                      <TabCount>{shouldShowEmptyState ? 0 : filteredCounts.none}</TabCount>
                    </span>
                  }
                />
                <Tab
                  disabled={confidenceDistribution?.low === 0}
                  eventKey="low"
                  title={
                    <span>
                      Low
                      <TabCount>{shouldShowEmptyState ? 0 : filteredCounts.low}</TabCount>
                    </span>
                  }
                />
                <Tab
                  disabled={confidenceDistribution?.medium === 0}
                  eventKey="medium"
                  title={
                    <span>
                      Medium
                      <TabCount>{shouldShowEmptyState ? 0 : filteredCounts.medium}</TabCount>
                    </span>
                  }
                />
                <Tab
                  disabled={confidenceDistribution?.high === 0}
                  eventKey="high"
                  title={
                    <span>
                      High
                      <TabCount>{shouldShowEmptyState ? 0 : filteredCounts.high}</TabCount>
                    </span>
                  }
                />
              </StyledTabs>

              <ViewToggleGroup>
                <IconButton
                  $active={imagesPerRow === 6}
                  onClick={() => setImagesPerRow(6)}
                  title="Small (6 per row)"
                >
                  <Grid3x3 />
                </IconButton>
                <IconButton
                  $active={imagesPerRow === 4}
                  onClick={() => setImagesPerRow(4)}
                  title="Medium (4 per row)"
                >
                  <Grid2x2 />
                </IconButton>
                <IconButton
                  $active={imagesPerRow === 2}
                  onClick={() => setImagesPerRow(2)}
                  title="Large (2 per row)"
                >
                  <LayoutGrid />
                </IconButton>
              </ViewToggleGroup>

              <StatsToggleButton
                onClick={() => setIsStatsOpen(!isStatsOpen)}
                className={!isStatsOpen ? 'collapsed' : ''}
              >
                <FaChartBar size={16} />
                Stats
                {isStatsOpen ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
              </StatsToggleButton>
            </ControlsRow>

            <StatsOverlayContainer $isOpen={isStatsOpen}>
              <CollapsibleStats>
                <InferenceStats
                  confidenceDistribution={confidenceDistribution}
                  confidenceRanges={confidenceRanges}
                  activeTab={activeConfidenceLevel || 'none'}
                  tags={tags?.tags || []}
                  onTabChange={(tab) => {
                    dispatch(setActiveConfidenceLevel(tab))
                  }}
                  onOpenSettings={handleOpenSettings}
                  bboxThreshold={BBOX_THRESHOLD}
                  shouldShowEmptyState={shouldShowEmptyState}
                />
              </CollapsibleStats>
            </StatsOverlayContainer>
          </HeaderContainer>

          <ContentWrapper>
            <LeftSection>
              <GalleryContainer>{renderPredictionsContent()}</GalleryContainer>
            </LeftSection>

            <RightSection>
              <ReviewProgressCard
                confidenceDistribution={confidenceDistribution}
                totalImages={allPredictions?.predictions?.length || 0}
                batchStartTime={selectedModel?.batch?.started_at}
                batchStatus={selectedModel?.batch?.status}
                originalUnlabeledCount={selectedModel?.batch?.total_content || 0}
                completedCount={selectedModel?.batch?.completed_content || 0}
                reviewedImages={
                  annotatedContent?.content?.map((content) => {
                    const boundingBoxes = content.annotation?.[0]?.metadata?.bounding_boxes || []
                    const orientedBBoxes =
                      content.annotation?.[0]?.metadata?.oriented_bounding_boxes || []

                    // For classification, synthesize "bboxes" from tagids
                    let bboxes = boundingBoxes.length > 0 ? boundingBoxes : orientedBBoxes
                    if (project?.annotation_type === 'classification' && bboxes.length === 0) {
                      // For classification, create placeholder "boxes" from tagids to show predicted classes
                      const tagIds = content.annotation?.[0]?.tagids || []
                      bboxes = tagIds.map((tagId, index) => ({
                        name: tagId,
                        points: [],
                        ai_labeled: content.annotation?.[0]?.ai_labeled || false,
                        class_index: index,
                        class_name: '', // Will be looked up by tag ID
                      })) as any
                    }

                    return {
                      contentid: content.id,
                      projectId: selectedProjectId || '',
                      datasetId: project?.datasetid || '',
                      width: content.width,
                      height: content.height,
                      bboxes,
                      annotation: content.annotation, // Pass full annotation array
                    }
                  }) || []
                }
              />
            </RightSection>
          </ContentWrapper>
        </MainContainer>
      )}

      {/* Model Assisted Labeling Modal*/}
      <Modal show={showModelModal} onHide={() => setShowModelModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: '#202020' }}>
          <Modal.Title style={{ backgroundColor: '#202020' }}>Available Models</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#202020' }}>
          <ModelAssistedLabeling onHide={() => setShowModelModal(false)} />
        </Modal.Body>
      </Modal>

      {/* Confidence Settings Modal */}
      <Modal
        show={showConfidenceSettings}
        onHide={() => setShowConfidenceSettings(false)}
        centered
        className="confidence-settings-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Adjust Confidence Thresholds</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="confidence-settings">
            <div className="setting-group">
              <label>High Confidence Threshold (Default: &lt; 80%)</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={tempConfidenceRanges.high * 100}
                  onChange={(e) =>
                    setTempConfidenceRanges((prev) => ({
                      ...prev,
                      high: Number(e.target.value) / 100,
                    }))
                  }
                  className="form-range"
                />
                <span className="value">{(tempConfidenceRanges.high * 100).toFixed(0)}%</span>
              </div>
            </div>
            <div className="setting-group">
              <label>Low Confidence Threshold (Default: &gt; 50%)</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={tempConfidenceRanges.medium * 100}
                  onChange={(e) =>
                    setTempConfidenceRanges((prev) => ({
                      ...prev,
                      medium: Number(e.target.value) / 100,
                    }))
                  }
                  className="form-range"
                />
                <span className="value">{(tempConfidenceRanges.medium * 100).toFixed(0)}%</span>
              </div>
            </div>
            <div className="mt-3 d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleResetSettings}>
                Reset to Defaults
              </Button>
              <Button variant="primary" onClick={handleSaveSettings}>
                Save Changes
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />
    </>
  )
}

export default InferenceAALSubView
